import { makeAutoObservable, runInAction } from "mobx";

import { history as routeHistory } from "..";
import Auth from "../api/Auth";
import { IForgotPasswordForm } from "../components/AuthPage/ForgotPasswordForm/ForgotPasswordForm";
import Constants from "../constants/Common";
import { FormConstructorAutoFillColumnFieldValuesMapping } from "../constants/Data";
import { UserOrgnaizationTypes } from "../constants/Enums";
import { StatusInfo } from "./core";
import { WithBooleanFlag } from "./core/WithBooleanFlag";
import { message } from "antd";
import { IUser } from "../interface/auth";

const mapperUserDataFromServer = (user: IUser) => {
  const unnecessaryNames = [
    "resourceType",
    "hasDefectNotifications",
    "orgProfileId",
  ];
  const organizationTypes = user.additionalParams.reduce(
    (acc, { name, value }) => {
      if (!unnecessaryNames.includes(name)) {
        acc[name] = value;
      }
      return acc;
    },
    <{ [key: string]: boolean }>{},
  );

  const { isGrbs, allOrgsTer, allOrgs, isRso } = organizationTypes;

  const values = Object.values(organizationTypes);

  const userIs = () => {
    if (values.every((val) => val)) {
      return UserOrgnaizationTypes.Admin;
    }
    if (allOrgsTer) {
      return UserOrgnaizationTypes.Keio;
    }
    if (allOrgs) {
      return UserOrgnaizationTypes.Ces;
    }
    if (isGrbs && !allOrgsTer) {
      return UserOrgnaizationTypes.Grbs;
    }
    if (values.every((val) => !val)) {
      return UserOrgnaizationTypes.Gu;
    }
    if (isRso) {
      return UserOrgnaizationTypes.Rso;
    }
    return null;
  };

  const userRole = userIs();

  const mappedUser = { ...user, userRole };

  return mappedUser;
};
export class AuthStore {
  userProfile = null as IUser;

  userProfileIsLoading = new WithBooleanFlag(true);

  passwordStatus = new StatusInfo();
  forgotPasswordStatus = new StatusInfo();
  messageStatus = new StatusInfo();

  authFormsIsLoading = new WithBooleanFlag(false);
  loading = false;

  loginError = null;
  userRights = [];
  allEdit = null;
  resourceType: number[] = [];
  defectNotify = null;
  allOrgsView = null;
  allLayersGis = null;
  isGrbs = null;
  isTer = null;
  isRso = null;
  isTko = null;

  onlyGrbs = null;

  esiaOrgs = null;
  esiaOid = null;
  sessionConfig = null;
  messageLogout = "";
  currentSystemTime = "";

  isEsiaEnabled = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  login = async (data, history) => {
    this.authFormsIsLoading.setTrue();
    try {
      const response = await Auth.login(data);
      runInAction(() => {
        if (response.status === 200) {
          localStorage.setItem("token", response.data.token);
          this.userProfile = response.data.profile;
          history.push("/");
        }
      });
    } catch (error) {
      runInAction(() => {
        const errStatusCode = error.response.status;
        if (errStatusCode === 400) {
          this.loginError = "Неверный логин или пароль";
        } else {
          this.loginError = "Не удалось авторизоваться";
        }
      });
    } finally {
      this.authFormsIsLoading.setFalse();
    }
  };

  setEsiaOid = (oid) => {
    this.esiaOid = oid;
  };

  getOrgListEsia = async (oid) => {
    this.authFormsIsLoading.setTrue();
    try {
      const response = await Auth.getEsiaOrgs(oid);
      runInAction(() => {
        this.esiaOrgs = response.data;
      });
    } catch (error) {
      runInAction(() => {});
    } finally {
      this.authFormsIsLoading.setFalse();
    }
  };

  loginByOrgEsia = async (orgName, history) => {
    this.authFormsIsLoading.setTrue();
    try {
      const response = await Auth.loginEsia(this.esiaOid, orgName);
      runInAction(async () => {
        if (response.data.success) {
          localStorage.setItem("token", response.data.token);
          this.userProfile = response.data.profile;
          await authStore.getUserInfo(history);
          history.push("/");
        } else {
          if (response.data.error) {
            message.error(response.data.error);
          }
          if (response.data.statusCode && response.data.statusCode == 1)
            history.push("/login-esia-no-email");
          else history.push("/login-esia");
        }
      });
    } catch (error) {
      runInAction(() => {});
    } finally {
      this.authFormsIsLoading.setFalse();
    }
  };

  logoutEsia = async (history) => {
    this.authFormsIsLoading.setTrue();
    try {
      const response = await Auth.logoutEsia();
      runInAction(() => {
        this.esiaOid = null;
        history.push("/login");
      });
    } catch (error) {
      runInAction(() => {});
    } finally {
      this.authFormsIsLoading.setFalse();
    }
  };

  checkEsiaEnabled = async () => {
    try {
      const res = await Auth.isEsiaEnabled();
      runInAction(() => {
        this.isEsiaEnabled = res.data;
      });
    } catch (error) {
      console.error(error);
    }
  };

  getLogoutUrl = () => {
    return Auth.getLogoutUrl();
  };

  getUserInfo = async (history) => {
    this.userProfileIsLoading.setTrue();

    try {
      const { data } = await Auth.getUserInfo();
      runInAction(() => {
        this.userProfile = mapperUserDataFromServer(data);

        localStorage.setItem("userOrgId", JSON.stringify(data?.organizationId));

        const profId = data.additionalParams.find(
          (el) => el.name === "orgProfileId",
        )?.value;

        this.allEdit = data.additionalParams?.find(
          (el) => el.name === "allOrgsEdit",
        )?.value;

        this.allOrgsView = data.additionalParams?.find(
          (el) => el.name === "allOrgs",
        )?.value;

        this.allLayersGis = data.additionalParams?.find(
          (el) => el.name === "allLayersGis",
        )?.value;

        this.isGrbs = data.additionalParams?.find(
          (el) => el.name === "isGrbs",
        )?.value;
        this.isTer = data.additionalParams?.find(
          (el) => el.name === "allOrgsTer",
        )?.value;

        this.defectNotify = data.additionalParams?.find(
          (el) => el.name === "hasDefectNotifications",
        )?.value;

        this.isRso = data.additionalParams?.find(
          (el) => el.name === "isRso",
        )?.value;

        this.isTko = data.additionalParams?.find(
          (el) => el.name === "isTko",
        )?.value;

        this.onlyGrbs = this.isGrbs && !this.isTer;

        // TODO ругается на несоотвествие типов
        const resourceTypes = data.additionalParams?.find(
          (el) => el.name === "resourceType",
        );
        this.resourceType = resourceTypes?.value?.length
          ? resourceTypes?.value
          : [];

        if (profId) {
          localStorage.setItem("userProfId", JSON.stringify(profId));
        }
      });
      // получение времени на сервере
      this.getCurrentSystemTime();
    } catch (error) {
      runInAction(() => {
        if (error.response) {
          const arrOfPaths = [
            "/reset-password",
            "/change-password",
            "/esia/choose-org",
          ];

          if (error.response.status === 401) {
            if (
              arrOfPaths.includes(
                routeHistory?.location?.pathname?.toLowerCase(),
              )
            ) {
              return;
            }
            routeHistory.push("/login");
          }
        }
      });
    } finally {
      this.userProfileIsLoading.setFalse();
    }
  };

  logout = async (history) => {
    try {
      await Auth.logout();
      runInAction(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("userOrgId");
        localStorage.removeItem("organizationId");
        localStorage.removeItem("userProfId");
        this.userProfile = null;
        history.push("/login");
      });
    } catch (error) {
      runInAction(() => {
        console.log(error);
        if (error?.response?.data?.message) {
          this.messageLogout = error.response.data.message;
        }
      });
    }
  };

  forgotPassword = (data: IForgotPasswordForm, history) => {
    this.authFormsIsLoading.setTrue();
    Constants.requestStatuses.error;
    Auth.requestResetPassword(data.email)
      .then(() => {
        this.forgotPasswordStatus.setDetails([
          "На указанный адрес электронной почты отправлено письмо с описанием дальнейших действий",
        ]);
        this.forgotPasswordStatus.setStatus(
          Constants.requestStatuses.notifySend,
        );
        history.push("/login");
      })

      .catch((err) => {
        if (err.response) {
          this.forgotPasswordStatus.setDetails(err);
          this.forgotPasswordStatus.setStatus(Constants.requestStatuses.error);
        }
      })
      .finally(() => {
        this.authFormsIsLoading.setFalse();
      });
  };

  changePassword = async (data, byURL, history) => {
    this.authFormsIsLoading.setTrue();
    if (byURL) {
      try {
        const res = await Auth.resetPassword(data);

        authStore.passwordStatus.setStatus("Новый пароль успешно установлен");
        history.push("/login");
      } catch (err) {
        if (err.response) {
          authStore.passwordStatus.setDetails(err);
          authStore.passwordStatus.setStatus("error");
        }
      } finally {
        this.authFormsIsLoading.setFalse();
      }
    } else {
      try {
        const res = await Auth.changePassword(data);

        if (res.data.succeeded) {
          this.passwordStatus.setStatus(
            "Новый пароль успешно установлен. При следующем входе используйте новый пароль",
          );
          return { success: true };
        } else {
          if (res.data.errors) {
            this.passwordStatus.setDetails([
              'Введен некорректный текущий пароль. Если вы забыли свой пароль, его можно сбросить со страницы авторизации по кнопке "Забыл пароль"',
            ]);
            this.passwordStatus.setStatus("error");
          }
          return { success: false };
        }
      } catch (err) {
        if (err.response) {
          this.passwordStatus.setDetails(err);
          this.passwordStatus.setStatus("error");
          return { success: false };
        }
      } finally {
        this.authFormsIsLoading.setFalse();
      }
    }
  };

  hasRight(right: string) {
    return runInAction(() => {
      return this.userProfile?.additionalParams?.find((el) => el.name === right)
        ?.value;
    });
  }

  hasRole(role: string) {
    return !!this.userProfile?.roles?.find((el) => el.name === role);
  }

  getFieldValueFromProfile(value) {
    const el = FormConstructorAutoFillColumnFieldValuesMapping.find(
      (item) => item.id === value,
    );

    if (el && el.key) {
      return this.userProfile?.[el.key];
    }
  }

  setSessionConfig = async (data) => {
    this.loading = true;
    try {
      await Auth.setSessionConfig(data);
      this.messageStatus.setStatus(Constants.statusMessage.updated);
    } catch (e) {
      this.messageStatus.setDetails(e);
      this.messageStatus.setStatus(Constants.statusMessage.error);
    } finally {
      this.loading = false;
    }
  };

  getSessionConfig = async () => {
    this.loading = true;
    try {
      const res = await Auth.getSessionConfig();

      runInAction(() => {
        this.sessionConfig = res.data;
      });
    } catch (e) {
      this.messageStatus.setDetails(e);
      this.messageStatus.setStatus(Constants.statusMessage.error);
    } finally {
      this.loading = false;
    }
  };

  clearMessageLogout = () => {
    this.messageLogout = "";
  };
  // время на сервера
  getCurrentSystemTime = async () => {
    try {
      const res = await Auth.getCurrentSystemTime();

      runInAction(() => {
        this.currentSystemTime = res.data;
      });
    } catch (error) {
      runInAction(() => {
        this.currentSystemTime = "";
      });
      console.log(error?.response?.data);
    }
  };
}

export const authStore = new AuthStore();
